import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import EditStudentSheet from "../components/EditStudentSheet";
import ScheduleEditSheet from "../components/ScheduleEditSheet";
import DocumentsSection from "../components/DocumentsSection";
import { Printer, CheckCircle2, ArrowLeft, LayoutDashboard, Pencil, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const SnapRow = ({ label, value, danger }) => (
  <div className={`rounded-lg p-3 ${danger ? "bg-red-50 border border-red-100" : "bg-slate-50"}`}>
    <p className={`text-[10px] font-bold uppercase tracking-[0.08em] ${danger ? "text-red-600" : "text-slate-500"}`}>{label}</p>
    <p className="text-sm text-slate-800 mt-1 leading-relaxed">{Array.isArray(value) ? value.join(" · ") : value || "—"}</p>
  </div>
);

export default function StudentProfile() {
  const { id } = useParams();
  const [student, setStudent] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [cmEdit, setCmEdit] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = () => api.get(`/students/${id}`).then((r) => setStudent(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [id]);

  if (!student) return <p className="text-slate-400 text-sm">Loading…</p>;
  const snap = student.snapshot;
  const prof = student.profile;
  const canEdit = ["tag_admin", "supervisor"].includes(user?.role);
  const canEditSchedule = canEdit || user?.role === "aide";
  const canUploadDocs = canEdit || user?.role === "aide";
  const canEditCaseManager = ["tag_admin", "sped_teacher"].includes(user?.role);

  const saveCaseManager = async () => {
    try {
      await api.put(`/students/${id}/case-manager`, { case_manager: cmEdit });
      toast.success("Case manager updated");
      setCmEdit(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const acknowledge = async () => {
    try {
      await api.post(`/students/${id}/acknowledge`);
      toast.success("Snapshot acknowledged — timestamped");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="no-print flex items-center justify-between flex-wrap gap-3">
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={16} /> Back</button>
        <div className="flex gap-2">
          {["tag_admin", "supervisor"].includes(user?.role) && (
            <button data-testid="edit-student-btn" onClick={() => setEditing(true)}
              className="flex items-center gap-1.5 text-xs font-semibold bg-amber-50 text-amber-800 hover:bg-amber-100 px-4 py-2.5 rounded-full transition-colors duration-150">
              <Pencil size={14} /> Edit
            </button>
          )}
          <button data-testid="open-dashboard-btn" onClick={() => navigate(`/students/${id}/dashboard`)}
            className="flex items-center gap-1.5 text-xs font-semibold bg-emerald-700 text-white hover:bg-emerald-800 px-4 py-2.5 rounded-full transition-colors duration-150">
            <LayoutDashboard size={14} /> Dashboard
          </button>
          <button data-testid="print-snapshot-btn" onClick={() => window.print()}
            className="flex items-center gap-1.5 text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-4 py-2.5 rounded-full transition-colors duration-150">
            <Printer size={14} /> Print Snapshot
          </button>
        </div>
      </div>

      <div className="print-area bg-white border border-slate-200/60 rounded-xl p-5 sm:p-8" data-testid="support-snapshot">
        <div className="flex items-start justify-between flex-wrap gap-2 border-b border-slate-100 pb-4 mb-5">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-emerald-700">Student Support Snapshot · v{snap.version}</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">{student.first_name} {student.last_name}</h1>
            <p className="text-sm text-slate-500">Grade {student.grade} · Updated {snap.updated_at?.slice(0, 10)}</p>
            <div className="mt-1.5 flex items-center gap-2 flex-wrap" data-testid="case-manager-row">
              <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500">Case Manager</span>
              {cmEdit === null ? (
                <>
                  <span data-testid="case-manager-value" className="text-sm text-slate-800">{student.case_manager || "—"}</span>
                  {canEditCaseManager && (
                    <button data-testid="edit-case-manager-btn" onClick={() => setCmEdit(student.case_manager || "")}
                      className="no-print text-xs font-semibold text-emerald-700 hover:underline">Edit</button>
                  )}
                </>
              ) : (
                <span className="no-print flex items-center gap-2">
                  <input data-testid="case-manager-input" value={cmEdit} onChange={(e) => setCmEdit(e.target.value)} placeholder="Case manager name"
                    className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
                  <button data-testid="save-case-manager-btn" onClick={saveCaseManager}
                    className="text-xs font-semibold bg-emerald-700 text-white px-3 py-1.5 rounded-full hover:bg-emerald-800">Save</button>
                  <button data-testid="cancel-case-manager-btn" onClick={() => setCmEdit(null)}
                    className="text-xs font-semibold text-slate-500 hover:text-slate-800">Cancel</button>
                </span>
              )}
            </div>
          </div>
          {student.my_ack ? (
            <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 px-3 py-2 rounded-full"><CheckCircle2 size={14} /> Acknowledged</span>
          ) : (
            <button data-testid="acknowledge-snapshot-btn" onClick={acknowledge}
              className="no-print bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold px-4 py-2.5 rounded-full transition-colors duration-150 active:scale-95">
              I have read this Snapshot
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SnapRow label="What helps this student succeed" value={snap.what_helps} />
          <SnapRow label="Common triggers for dysregulation" value={snap.triggers} />
          <SnapRow label="How to redirect" value={snap.how_to_redirect} />
          <SnapRow label="Expected prompting level" value={snap.expected_prompting} />
          <SnapRow label="Required accommodations" value={snap.accommodations} />
          <SnapRow label="What NOT to do" value={snap.what_not_to_do} danger />
          <SnapRow label="Who to contact when an issue occurs" value={snap.contact_on_issue} />
          <SnapRow label="Current weekly priorities" value={snap.weekly_priorities} />
        </div>
      </div>

      <div className="no-print grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200/60 rounded-xl p-5 sm:p-6 space-y-3">
          <h3 className="font-heading font-semibold text-slate-800">Support Profile</h3>
          <SnapRow label="Support areas" value={prof.support_areas} />
          <SnapRow label="Behavior support strategies" value={prof.behavior_strategies} />
          <SnapRow label="Prompting guidance" value={prof.prompting_guidance} />
          <SnapRow label="Safety / elopement protocols" value={prof.safety_protocols} danger={/elopement risk/i.test(prof.safety_protocols || "")} />
          <SnapRow label="Communication preferences" value={prof.communication_prefs} />
          <SnapRow label="Key contacts" value={(prof.contacts || []).map((c) => `${c.name} (${c.role}) ${c.phone || ""}`)} />
        </div>
        <div className="bg-white border border-slate-200/60 rounded-xl p-5 sm:p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-semibold text-slate-800">Daily Schedule</h3>
            {canEditSchedule && (
              <button data-testid="edit-schedule-btn" onClick={() => setEditingSchedule(true)}
                className="flex items-center gap-1.5 text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 px-3.5 py-2 rounded-full transition-colors duration-150">
                <CalendarClock size={14} /> Edit schedule
              </button>
            )}
          </div>
          <ul className="divide-y divide-slate-100">
            {(prof.schedule || []).map((b) => (
              <li key={b.block_id} className="py-2.5 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-800">{b.name}</p>
                  <p className="text-xs text-slate-400">{b.location}</p>
                </div>
                <p className="text-xs font-mono text-slate-500">{b.start}–{b.end}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <DocumentsSection studentId={id} canEdit={canUploadDocs} canDelete={canEdit} />

      {editing && <EditStudentSheet student={student} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
      {editingSchedule && <ScheduleEditSheet student={student} onClose={() => setEditingSchedule(false)} onSaved={() => { setEditingSchedule(false); load(); }} />}
    </div>
  );
}
